import * as React from 'react';

import {useDroppable} from '@dnd-kit/react';

import {classify} from 'src/utils/classify';

import {ROW_END_SUFFIX} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/utils/handle-drag-end';
import type {DragType} from './dnd-config';
import layoutCss from './dashboard-builder-canvas-layout.css';


export type RowEndDropZoneProps = {
  rowId: string;
  dropType: DragType;
  activeDragType: DragType | null;
  /**
   * True when this row is at its tile-count cap for an incoming other-row
   * drag — collapses the end-of-row slot so it isn't presented as a drop
   * target the row can't honor.
   */
  isAtCapacity: boolean;
  /**
   * True when the active drag originated from this row. Kept for
   * callers that may want to style differently, but does NOT disable
   * the drop zone — with OptimisticSortingPlugin off, the end zone is
   * the only way to move a tile past the last item in a same-row
   * reorder (especially in max-capacity rows).
   */
  isSourceInThisRow: boolean;
};

/**
 * Invisible sentinel droppable that fills remaining row width so drops
 * past the last tile register correctly. `useSortable` items only catch
 * collisions inside their own bounds, so without this sentinel a drop in
 * the empty area to the right of the last tile would miss every target.
 */
export function RowEndDropZone({
  rowId,
  dropType,
  activeDragType,
  isAtCapacity,
  isSourceInThisRow,
}: RowEndDropZoneProps) {
  const isCompatible =
    activeDragType !== null && activeDragType === dropType && !isAtCapacity;
  const {ref} = useDroppable({
    id: `${rowId}${ROW_END_SUFFIX}`,
    accept: dropType,
    data: {type: dropType, rowId},
    disabled: !isCompatible,
  });

  return (
    <div
      ref={ref}
      className={classify(
        layoutCss.rowEndDropZone,
        isCompatible && !isSourceInThisRow && layoutCss.rowEndDropZoneActive,
        isCompatible && isSourceInThisRow && layoutCss.rowEndDropZoneSameRow,
      )}
    />
  );
}
